// packages/web/src/pages/LandingPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Grid, 
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

// Styled Components
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: theme.spacing(12, 0, 10, 0),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(8, 0, 6, 0),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
}));

const LogoImage = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d82f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(132, 184, 23, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: '#c4d82f',
    top: 8,
    left: 8,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#679614',
    bottom: 8,
    right: 8,
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a1a1a',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  letterSpacing: '-0.03em',
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 700,
  lineHeight: 1.15,
  marginBottom: theme.spacing(3),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.75rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: '#666',
  marginBottom: theme.spacing(5),
  lineHeight: 1.7,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1.05rem',
  fontWeight: 700,
  padding: theme.spacing(1.75, 5),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '320px',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '1.05rem',
  fontWeight: 600,
  padding: theme.spacing(1.75, 5),
  borderRadius: '8px',
  border: '2px solid #1a1a1a',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '320px',
  },
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  backgroundColor: '#fafafa',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.75rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.875rem',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  textAlign: 'center',
  color: '#666',
  marginBottom: theme.spacing(7),
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(5),
    fontSize: '1rem',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid #e5e5e5',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
    transform: 'translateY(-6px)',
    borderColor: '#c4d600',
  },
}));

const FeatureIconWrapper = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '16px',
  backgroundColor: '#f0f8e8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2.5),
  '& svg': {
    fontSize: 36,
    color: '#84b817',
  },
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.375rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
}));

const LearnMore = styled(Typography)(({ theme }) => ({
  color: '#84b817',
  fontSize: '0.95rem',
  fontWeight: 600,
  marginTop: theme.spacing(2),
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '&:hover': {
    color: '#679614',
    textDecoration: 'underline',
  },
  transition: 'all 0.2s ease',
}));

const CTASection = styled(Box)(({ theme }) => ({
  backgroundColor: '#c4d600',
  padding: theme.spacing(10, 0),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2.5),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.875rem',
  },
}));

const CTASubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: '#1a1a1a',
  marginBottom: theme.spacing(4),
  fontWeight: 400,
  opacity: 0.9,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#1a1a1a',
  fontSize: '1.05rem',
  fontWeight: 700,
  padding: theme.spacing(1.75, 5),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '320px',
  },
}));

const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 4, 0),
  borderTop: '1px solid #e5e5e5',
}));

const FooterColumn = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2.5),
  },
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: '#666',
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'color 0.2s',
  fontWeight: 400,
  '&:hover': {
    color: '#84b817',
  },
}));

const Copyright = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 0, 3, 0),
  borderTop: '1px solid #e5e5e5',
  marginTop: theme.spacing(4),
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: '#e5e5e5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '& svg': {
    fontSize: 20,
    color: '#666',
  },
  '&:hover': {
    backgroundColor: '#84b817',
    transform: 'translateY(-2px)',
    '& svg': {
      color: '#fff',
    },
  },
}));

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const handleGetStarted = () => {
    navigate('/calendar');
  };

  // Same pattern as CourtsPage and other pages
  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const features = [
    {
      icon: <ShareIcon />,
      title: 'Publish Your Schedule',
      description: 'Share your tennis availability with other players. Let them see when you\'re free to play',
      hasLearnMore: false,
    },
    {
      icon: <VisibilityIcon />,
      title: 'Find Available Players',
      description: 'Browse other players\' schedules to find the perfect match time that works for both of you',
      hasLearnMore: false,
    },
    {
      icon: <EventIcon />,
      title: 'Coordinate Matches Instantly',
      description: 'No more back-and-forth messaging. See availability and book matches in seconds',
      hasLearnMore: false,
    },
    {
      icon: <GroupIcon />,
      title: 'Challenge Players',
      description: 'Send match requests to players based on their published availability and skill level',
      hasLearnMore: false,
    },
    {
      icon: <AccessTimeIcon />,
      title: 'Manage Your Time',
      description: 'Keep track of upcoming matches, pending challenges, and your tennis schedule all in one place',
      hasLearnMore: false,
    },
  ];

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="md">
          <LogoContainer>
            <LogoImage />
            <LogoText>sched tennis</LogoText>
          </LogoContainer>

          <HeroTitle>
            Share Your Tennis Schedule.<br />
            Let other players find and<br />
            challenge you for the game.
          </HeroTitle>

          <HeroSubtitle>
            Publish your availability, discover when others can play,<br />
            and coordinate matches without the hassle.
          </HeroSubtitle>

          <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <PrimaryButton onClick={handleGetStarted}>
              Publish My Schedule →
            </PrimaryButton>
            <SecondaryButton onClick={handleLoginClick}>
              Sign In
            </SecondaryButton>
          </Box>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <FeaturesSection>
        <Container maxWidth="lg">
          <SectionTitle>
            Stop Playing Phone Tag. Start Playing Tennis.
          </SectionTitle>
          <SectionSubtitle>
            Share your schedule, see when others are available, and coordinate matches effortlessly
          </SectionSubtitle>

          <Grid container spacing={3.5}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard>
                  <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <FeatureIconWrapper>
                      {feature.icon}
                    </FeatureIconWrapper>
                    <FeatureTitle>
                      {feature.title}
                    </FeatureTitle>
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                    {feature.hasLearnMore && (
                      <LearnMore>
                        Learn more →
                      </LearnMore>
                    )}
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </FeaturesSection>

      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="md">
          <CTATitle>
            Ready to Share Your Schedule?
          </CTATitle>
          <CTASubtitle>
            Join tennis players who are finding more matches by publishing their availability
          </CTASubtitle>
          <CTAButton onClick={handleGetStarted}>
            Get Started Free
          </CTAButton>
        </Container>
      </CTASection>

      {/* Footer Section */}
      <Footer />

      {/* Login Dialog - Same pattern as CourtsPage */}
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default LandingPage;